import React, { useState } from 'react';
import { IntlShape, injectIntl } from 'react-intl';
import {
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Typography,
} from '@material-ui/core';
import styled from 'styled-components';
import { Listings, SagisConfig } from 'containers/AppContainer/typings';
import { FormContainer } from 'containers/CourtManagerLegalProceedings/AssignExpert/styled-components';
import messages from 'containers/PreOpeningPage/messages';
import { arrayTranslate, translate } from 'containers/services/translate';
import { BENEFICIARY, EMPTY_VICTIM, VICTIM } from 'containers/data-structure';
import {
  GENDER,
  INJURY_POST,
  MINOR_VALUE,
  VICTIM_ITT_AVAILABILITY,
} from 'containers/PreOpeningPage/constants';
import { get, isEmpty, isNil, uniqueId } from 'lodash';
import {
  KeyboardDatePicker,
  MuiPickersUtilsProvider,
} from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';
import frLocale from 'date-fns/locale/fr';
import ReactInputMask from 'react-input-mask';
import { filterVisibleOnly } from 'containers/services/filters';
import { Autocomplete, Autocomplete as AutoComplete } from '@material-ui/lab';
import { getCity, getMoroccanCities } from 'containers/RecapManagerPage/utils';
import { OTHER_PROFESSION } from 'containers/RecapManagerPage/constants';
import * as featureService from 'features/fflip-helpers';
import { FEATURES } from 'features/features';
import { VICTIM_QUALITY_KEYS } from 'containers/CourtManagerLegalProceedings/constants';
import { displayVehiclePassenger } from 'services/utils';
import { getDateIgnoreTimezone } from 'containers/common/LegalProceedings/utils';
import { dateStringToLocalDate } from 'containers/IdentificationPage/utils';
import PrimaryButton from 'components/Buttons/PrimaryButton';
import BeneficiaryAccordion from 'containers/PreOpeningPage/BeneficiaryAccordion';
import { Beneficiary } from 'containers/PreOpeningPage/typings';
import { displayErrorText } from 'containers/services/validateForm';

const ContentWrapper = styled(Grid)`
  margin-bottom: 16px;
`;
export interface VictimFormProps {
  intl: IntlShape;
  inputLabelProps?: boolean;
  listings: Listings;
  inputProps?: any;
  victim: any;
  setVictim: any;
  formValidator: any;
}

const StyledSelect = styled(Select)`
  &.MuiInput-underline.Mui-disabled:before {
    border-bottom-style: solid;
  }
  .MuiSelect-icon {
    color: black;
  }
  font-weight: 500;
`;

const VictimForm: React.FC<VictimFormProps> = props => {
  const { victim, setVictim, formValidator } = props;
  const [openBeneficiaryForm, setOpenBeneficiaryForm] = useState(false);
  const handleChange = (fieldName: string) => (
    event: React.ChangeEvent<{
      name?: string;
      value?: any;
    }>,
    valueField?: any,
  ) => {
    if (fieldName === VICTIM.ITT) {
      return setVictim({
        ...victim,
        [fieldName]: event.target.value.replace(/[^0-9]/g, ''),
      });
    }
    if (fieldName === VICTIM.TELEPHONE_NUMBER) {
      return setVictim({
        ...victim,
        [fieldName]: event.target.value.replace(/\s+/g, ''),
      });
    }
    if (fieldName === VICTIM.PROFESSION_TYPE) {
      return setVictim({
        ...victim,
        [fieldName]: get(valueField, 'ItemKey'),
        [VICTIM.PROFESSION]:
          get(valueField, 'ItemKey') === OTHER_PROFESSION
            ? get(victim, VICTIM.PROFESSION)
            : null,
      });
    }
    if (
      fieldName === VICTIM.CLASS_KEY &&
      get(victim, [VICTIM.CLASS_KEY]) === MINOR_VALUE
    ) {
      return setVictim({
        ...victim,
        [fieldName]: event.target.value,
        [VICTIM.NUMBER_OF_SPOUSE]: 0,
        [VICTIM.NUMBER_OF_DEPENDENTS]: 0,
        [VICTIM.NUMBER_OF_CHILDREN]: 0,
      });
    }
    if (
      fieldName === VICTIM.QUALITY_KEY &&
      featureService.hasFeature(FEATURES.IS_VICTIM_EXCLUDED_FROM_COVERAGE)
    ) {
      const isExludedVictim: boolean =
        event.target.value === VICTIM_QUALITY_KEYS.Driver ||
        event.target.value === VICTIM_QUALITY_KEYS.Subscriber;
      return setVictim({
        ...victim,
        [VICTIM.IS_EXCLUDED]: isExludedVictim,
        [fieldName]: event.target.value,
      });
    }
    setVictim({
      ...victim,
      [fieldName]: event.target.value,
    });
  };
  const handleDateChange = (fieldName: string) => (date: Date | null) => {
    const theDate = getDateIgnoreTimezone(date);
    setVictim({ ...victim, [fieldName]: theDate });
  };
  const handleCityChange = (fieldName: string) => (
    event: React.ChangeEvent<{
      name?: string;
      value?: any;
    }>,
    newValue: SagisConfig | null,
  ) => {
    setVictim({ ...victim, [fieldName]: newValue?.ItemKey });
  };
  const openFormBeneficiary = () => {
    setOpenBeneficiaryForm(true);
  };

  const saveBeneficiary = (beneficiary: Beneficiary) => {
    victim[VICTIM.BENEFICIARIES].push(beneficiary as never);
    setVictim({ ...victim });
  };

  const translateArrayErrorMessages = arrayTranslate(props.intl.formatMessage);
  const { message } = displayErrorText(
    formValidator,
    translateArrayErrorMessages(formValidator, true),
  );

  return (
    <FormContainer>
      <div className="subContainer">
        <FormControl>
          <InputLabel
            id="simple-select-outlined-label-procedure"
            shrink={props.inputLabelProps}
          >
            {translate({ ...messages.victimInjuryPostKey }, props)}
          </InputLabel>
          <Select
            className="input"
            fullWidth
            name={VICTIM.INJURY_POST_KEY}
            value={
              victim.injuryPostKey === INJURY_POST.INJURED[1]
                ? INJURY_POST.INJURED[0]
                : victim.injuryPostKey
            }
            onChange={handleChange(VICTIM.INJURY_POST_KEY)}
            labelWidth={230}
          >
            {!isEmpty(get(props.listings, 'prejudice', [])) &&
              props
                .listings!.prejudice.filter(
                  (el: any) => el.ItemKey !== INJURY_POST.INJURED[1],
                )
                .map(e => (
                  <MenuItem value={e.ItemKey} key={e.ItemKey}>
                    {get(e.ItemDescription.split(' '), '[0]', '')}
                  </MenuItem>
                ))}
          </Select>
        </FormControl>
        <FormControl>
          <TextField
            fullWidth
            label={translate({ ...messages.victimCin }, props)}
            name={VICTIM.CIN}
            value={victim.cin}
            onChange={handleChange(VICTIM.CIN)}
            InputLabelProps={{ shrink: props.inputLabelProps }}
            className="input"
          />
        </FormControl>
        <FormControl>
          <TextField
            data-testid={'last-name-input'}
            fullWidth
            label={translate({ ...messages.victimLastName }, props)}
            name={VICTIM.LAST_NAME}
            value={victim.lastName}
            onChange={handleChange(VICTIM.LAST_NAME)}
            className="input"
            error={!!get(formValidator, VICTIM.LAST_NAME)}
            helperText={
              get(formValidator, VICTIM.LAST_NAME)
                ? message[VICTIM.LAST_NAME]
                : ''
            }
          />
        </FormControl>
        <FormControl>
          <TextField
            data-testid={'first-name-input'}
            className="input"
            fullWidth
            label={translate({ ...messages.victimFirstName }, props)}
            name={VICTIM.FIRST_NAME}
            value={victim.firstName}
            onChange={handleChange(VICTIM.FIRST_NAME)}
            error={!!get(formValidator, VICTIM.FIRST_NAME)}
            helperText={
              get(formValidator, VICTIM.FIRST_NAME)
                ? message[VICTIM.FIRST_NAME]
                : ''
            }
          />
        </FormControl>
        <FormControl>
          <MuiPickersUtilsProvider utils={DateFnsUtils} locale={frLocale}>
            <KeyboardDatePicker
              className="input"
              fullWidth
              autoOk
              variant="inline"
              format="dd/MM/yyyy"
              id="date-picker-inline-victim-date-of-birth"
              label={translate({ ...messages.victimDateOfBirth }, props)}
              name={VICTIM.DATE_OF_BIRTH}
              value={dateStringToLocalDate(
                get(victim, 'dateOfBirth', null) as any,
              )}
              onChange={handleDateChange(VICTIM.DATE_OF_BIRTH)}
            />
          </MuiPickersUtilsProvider>
        </FormControl>
        <FormControl>
          <ReactInputMask
            mask="9999 999 999"
            value={victim.telephoneNumber}
            onChange={handleChange(VICTIM.TELEPHONE_NUMBER)}
          >
            {() => (
              <TextField
                label={translate({ ...messages.victimTelephone }, props)}
                className="input"
                data-testid={'telephone-number-input'}
              ></TextField>
            )}
          </ReactInputMask>
        </FormControl>
        <FormControl fullWidth>
          <InputLabel
            id="simple-select-outlined-label-procedure"
            shrink={props.inputLabelProps}
          >
            {translate({ ...messages.victimQualityKey }, props)}
          </InputLabel>
          <Select
            className="input"
            fullWidth
            name={VICTIM.QUALITY_KEY}
            value={victim.qualityKey}
            onChange={handleChange(VICTIM.QUALITY_KEY)}
          >
            {!isEmpty(get(props.listings, 'qualityOfVictim', [])) &&
              props.listings!.qualityOfVictim.map(classItem => (
                <MenuItem value={classItem.ItemKey} key={classItem.ItemKey}>
                  {classItem.ItemDescription}
                </MenuItem>
              ))}
          </Select>
        </FormControl>
        {displayVehiclePassenger(victim as any) && (
          <FormControl>
            <FormControl className="input">
              <InputLabel id="simple-select-outlined-label-procedure">
                {translate({ ...messages.vehiclePassenger }, props)}
              </InputLabel>
              <Select
                className="input"
                name={VICTIM.VEHICLE_PASSENGER_ID}
                value={get(victim, [VICTIM.VEHICLE_PASSENGER_ID], '')}
                onChange={handleChange(VICTIM.VEHICLE_PASSENGER_ID)}
              >
                {get(props.listings, 'vehiclePassenger', []).map(
                  (classItem: any) => (
                    <MenuItem value={classItem.id} key={classItem.id}>
                      {classItem.title}
                    </MenuItem>
                  ),
                )}
              </Select>
            </FormControl>
          </FormControl>
        )}
        <FormControl fullWidth>
          <InputLabel
            id="simple-select-outlined-label-class"
            shrink={props.inputLabelProps}
          >
            {translate({ ...messages.class }, props)}
          </InputLabel>
          <Select
            className="input"
            fullWidth
            name={VICTIM.CLASS_KEY}
            value={victim.classKey}
            onChange={handleChange(VICTIM.CLASS_KEY)}
          >
            {!isEmpty(get(props.listings, 'classVictim', [])) &&
              props.listings!.classVictim.map(classItem => (
                <MenuItem value={classItem.ItemKey} key={classItem.ItemKey}>
                  {classItem.ItemDescription}
                </MenuItem>
              ))}
          </Select>
        </FormControl>

        <FormControl className="input input-gender">
          <InputLabel
            id="simple-select-outlined-label-procedure"
            shrink={props.inputLabelProps}
          >
            {translate({ ...messages.sexe }, props)}
          </InputLabel>
          <StyledSelect
            name={VICTIM.GENDER}
            value={get(victim, [VICTIM.GENDER], '')}
            onChange={handleChange(VICTIM.GENDER)}
            labelWidth={230}
          >
            <MenuItem value={GENDER.MALE} key={0}>
              {GENDER.MALE}
            </MenuItem>
            <MenuItem value={GENDER.FEMALE} key={1}>
              {GENDER.FEMALE}
            </MenuItem>
          </StyledSelect>
        </FormControl>
        <FormControl fullWidth>
          <InputLabel
            id="simple-select-outlined-label-procedure"
            shrink={props.inputLabelProps}
          >
            {translate({ ...messages.typeProcedure }, props)}
          </InputLabel>
          <Select
            className="input"
            fullWidth
            name={VICTIM.PROCEDURE_ID}
            value={victim.procedureId}
            onChange={handleChange(VICTIM.PROCEDURE_ID)}
            labelWidth={230}
          >
            {!isEmpty(get(props.listings, 'procedures', [])) &&
              filterVisibleOnly(props.listings!.procedures).map(procedure => (
                <MenuItem value={procedure.id} key={procedure.id}>
                  {procedure.title}
                </MenuItem>
              ))}
          </Select>
        </FormControl>
        <FormControl>
          <AutoComplete
            className="input"
            options={get(props.listings, 'Profession', [])}
            getOptionLabel={(option: any) =>
              `${option?.ItemDescription ? option?.ItemDescription : ''}`
            }
            value={
              get(props.listings, 'Profession', []).find(
                (item: any) =>
                  item.ItemKey === get(victim, VICTIM.PROFESSION_TYPE),
              ) || {}
            }
            onChange={handleChange(VICTIM.PROFESSION_TYPE)}
            renderInput={(params: any) => (
              <TextField
                {...params}
                label={translate(messages.professionType, props)}
                data-testid={'profession-type-input'}
              />
            )}
          />
        </FormControl>
        <FormControl fullWidth>
          <InputLabel
            id="simple-select-outlined-label-work-accident"
            shrink={props.inputLabelProps}
          >
            {translate({ ...messages.workAccident }, props)}
          </InputLabel>
          <Select
            className="input"
            fullWidth
            name={VICTIM.WORK_ACCIDENT_ID}
            value={victim.workAccidentId}
            onChange={handleChange(VICTIM.WORK_ACCIDENT_ID)}
            labelWidth={140}
          >
            {!isEmpty(get(props.listings, 'workAccidents', [])) &&
              props.listings!.workAccidents.map(type => (
                <MenuItem value={type.id} key={type.id}>
                  {type.title}
                </MenuItem>
              ))}
          </Select>
        </FormControl>
        {get(victim, [VICTIM.INJURY_POST_KEY], '') !== '' &&
          !INJURY_POST.DECEASED.includes(
            get(victim, [VICTIM.INJURY_POST_KEY], ''),
          ) && (
            <>
              <FormControl>
                <InputLabel
                  id="simple-select-outlined-label-procedure"
                  shrink={props.inputLabelProps}
                >
                  {translate({ ...messages.availabilityItt }, props)}
                </InputLabel>
                <Select
                  className="input"
                  fullWidth
                  name={VICTIM.ITT_AVAILABILITY}
                  value={get(victim, [VICTIM.ITT_AVAILABILITY], null)}
                  onChange={handleChange(VICTIM.ITT_AVAILABILITY)}
                >
                  <MenuItem
                    value={VICTIM_ITT_AVAILABILITY.AVAILABLE}
                    key={uniqueId()}
                  >
                    {translate({ ...messages.available }, props)}
                  </MenuItem>
                  <MenuItem
                    value={VICTIM_ITT_AVAILABILITY.NOT_AVAILABLE}
                    key={uniqueId()}
                  >
                    {translate({ ...messages.notAvailable }, props)}
                  </MenuItem>
                </Select>
              </FormControl>
              {(get(victim, [VICTIM.ITT_AVAILABILITY]) ===
                VICTIM_ITT_AVAILABILITY.AVAILABLE ||
                (!isNil(get(victim, [VICTIM.ITT], null)) &&
                  get(victim, [VICTIM.ITT]) !== '')) && (
                <FormControl>
                  <TextField
                    className="input"
                    fullWidth
                    type="number"
                    label={translate({ ...messages.ITT }, props)}
                    name={VICTIM.ITT}
                    value={parseInt((victim.itt as unknown) as string)}
                    onChange={handleChange(VICTIM.ITT)}
                    InputLabelProps={{
                      shrink: props.inputLabelProps,
                    }}
                    data-testid={'itt-input'}
                  />
                </FormControl>
              )}
            </>
          )}
        <FormControl fullWidth>
          <InputLabel
            id="simple-select-outlined-label-work-accident"
            shrink={props.inputLabelProps}
          >
            {translate({ ...messages.victimOpposition }, props)}
          </InputLabel>
          <Select
            className="input"
            fullWidth
            name={VICTIM.OPPOSITION}
            value={get(victim, [VICTIM.OPPOSITION], null)}
            onChange={handleChange(VICTIM.OPPOSITION)}
            labelWidth={140}
          >
            <MenuItem value={0} key={uniqueId('oppos_')}>
              {translate({ ...messages.claimDeclarationNo }, props)}
            </MenuItem>
            <MenuItem value={1} key={uniqueId()}>
              {translate({ ...messages.claimDeclarationYes }, props)}
            </MenuItem>
          </Select>
        </FormControl>
        <FormControl>
          <Autocomplete
            className="input"
            id="standard-basic-2"
            options={getMoroccanCities(get(props.listings, 'villes', []))}
            getOptionLabel={city => city.ItemDescription}
            onChange={handleCityChange(VICTIM.CITY)}
            autoSelect={true}
            value={getCity(get(props.listings, 'villes', []), '')}
            renderInput={params => (
              <TextField
                {...params}
                name={VICTIM.CITY}
                label={translate({ ...messages.victimCity }, props)}
                data-testid={'city-input'}
              />
            )}
          />
        </FormControl>
        <FormControl>
          <TextField
            className="input"
            fullWidth
            label={translate({ ...messages.adress }, props)}
            name={VICTIM.ADDRESS}
            value={victim.address}
            onChange={handleChange(VICTIM.ADDRESS)}
            InputLabelProps={{ shrink: props.inputLabelProps }}
          />
        </FormControl>
        {INJURY_POST.DECEASED.includes(
          get(victim, [VICTIM.INJURY_POST_KEY], ''),
        ) && (
          <>
            <FormControl>
              <TextField
                className="input"
                type="number"
                label={translate({ ...messages.numberOfPartners }, props)}
                fullWidth
                name={VICTIM.NUMBER_OF_SPOUSE}
                value={get(victim, [VICTIM.NUMBER_OF_SPOUSE])}
                onChange={handleChange(VICTIM.NUMBER_OF_SPOUSE)}
                InputLabelProps={{
                  shrink: props.inputLabelProps,
                }}
              />
            </FormControl>
            <FormControl>
              <TextField
                className="input"
                label={translate({ ...messages.numberOfChildren }, props)}
                type="number"
                fullWidth
                name={VICTIM.NUMBER_OF_CHILDREN}
                value={get(victim, [VICTIM.NUMBER_OF_CHILDREN])}
                onChange={handleChange(VICTIM.NUMBER_OF_CHILDREN)}
                InputLabelProps={{
                  shrink: props.inputLabelProps,
                }}
              />
            </FormControl>
            <FormControl>
              <TextField
                type="number"
                fullWidth
                label={translate({ ...messages.numberOfDependents }, props)}
                className="input"
                name={VICTIM.NUMBER_OF_DEPENDENTS}
                value={get(victim, [VICTIM.NUMBER_OF_DEPENDENTS])}
                onChange={handleChange(VICTIM.NUMBER_OF_DEPENDENTS)}
                InputLabelProps={{
                  shrink: props.inputLabelProps,
                }}
              />
            </FormControl>
            <FormControl>
              <TextField
                className="input"
                label={translate({ ...messages.annualOfIncome }, props)}
                fullWidth
                name={VICTIM.ANNUAL_INCOME}
                value={get(victim, [VICTIM.ANNUAL_INCOME])}
                onChange={handleChange(VICTIM.ANNUAL_INCOME)}
                InputLabelProps={{
                  shrink: props.inputLabelProps,
                }}
              />
            </FormControl>
          </>
        )}
        {INJURY_POST.DECEASED.includes(
          get(victim, [VICTIM.INJURY_POST_KEY], ''),
        ) && (
          <div className="deceaseBlock">
            <Typography className="text deceased-title">
              <PrimaryButton
                data-testid="add_beneficiary_btn"
                onClick={() => openFormBeneficiary()}
                title={translate({ ...messages.addBeneficiaryBtn }, props)}
              />
            </Typography>
          </div>
        )}
        {openBeneficiaryForm && (
          <BeneficiaryAccordion
            listing={props.listings}
            isDrawer={false}
            isDrawerAdd={true}
            beneficiary={
              {
                [BENEFICIARY.DEPENDENT_INDICATOR]: true,
              } as any
            }
            inputProps={props.inputProps}
            disableInput={false}
            inputLabelProps={props.inputLabelProps}
            handleBeneficiaryChange={saveBeneficiary}
            victim={victim as any}
            isProvisional
          />
        )}
      </div>
    </FormContainer>
  );
};
export default injectIntl(VictimForm);
